// React Optimized Image Loader
class ReactImageLoader {
  private cache = new Map<string, string>();
  private observer: IntersectionObserver | null = null;
  private loadingQueue = new Set<string>();
  private maxConcurrent = 3;

  constructor() {
    this.init();
  }

  private init() {
    // Initialize intersection observer for lazy loading
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target as HTMLImageElement);
            this.observer?.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '100px 0px', // Start loading 100px before visible
        threshold: 0.01
      }
    );

    // Apply optimizations to existing images
    this.optimizeExistingImages();
  }

  private optimizeExistingImages() {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      this.optimizeImage(img);
    });
  }

  private optimizeImage(img: HTMLImageElement) {
    // Skip if already optimized
    if (img.dataset.optimized === 'true') return;
    
    const originalSrc = img.src;
    if (!originalSrc) return;

    // Add loading state
    img.classList.add('image-loading');
    
    // Disable lazy loading: always load immediately
    this.loadImage(img);

    img.dataset.optimized = 'true';
  }

  private loadImage(img: HTMLImageElement) {
    const src = img.dataset.src || img.src;
    if (!src || this.loadingQueue.has(src)) return;

    // Add to loading queue
    this.loadingQueue.add(src);

    // Check cache first
    if (this.cache.has(src)) {
      this.applyCachedImage(img, src);
      this.loadingQueue.delete(src);
      return;
    }

    // Create optimized URL
    const optimizedSrc = this.getOptimizedImageUrl(src);
    
    // Load with progressive enhancement
    this.loadProgressiveImage(img, optimizedSrc, src);
  }

  private loadProgressiveImage(img: HTMLImageElement, optimizedSrc: string, originalSrc: string) {
    // Check if img element is valid
    if (!img) {
      console.error('❌ Cannot set properties of null (setting src) - img element is null');
      return;
    }

    // First, try to load optimized version
    const tempImg = new Image();
    
    tempImg.onload = () => {
      if (img && img.src !== undefined) {
        img.src = optimizedSrc;
        img.classList.remove('image-loading', 'lazy');
        img.classList.add('image-loaded');
        this.cache.set(originalSrc, optimizedSrc);
        this.loadingQueue.delete(originalSrc);
      }
    };

    tempImg.onerror = () => {
      // Fallback to original if optimized fails
      if (img && img.src !== undefined) {
        img.src = originalSrc;
        img.classList.remove('image-loading', 'lazy');
        img.classList.add('image-loaded');
        this.cache.set(originalSrc, originalSrc);
        this.loadingQueue.delete(originalSrc);
      }
    };

    tempImg.src = optimizedSrc;
  }

  private applyCachedImage(img: HTMLImageElement, src: string) {
    if (!img) {
      console.error('❌ Cannot set properties of null (setting src) - img element is null');
      return;
    }

    const cachedSrc = this.cache.get(src);
    if (cachedSrc && img.src !== undefined) {
      img.src = cachedSrc;
      img.classList.remove('image-loading', 'lazy');
      img.classList.add('image-loaded');
    }
  }

  private getOptimizedImageUrl(originalUrl: string): string {
    if (!originalUrl) return originalUrl;

    // For S3 signed URLs, we can't modify them
    if (originalUrl.includes('amazonaws.com') || originalUrl.includes('s3.')) {
      return originalUrl;
    }

    // For local images, we could add size parameters
    // This is a placeholder for future CDN integration
    return originalUrl;
  }

  private getPlaceholderUrl(): string {
    // Return a tiny, cached placeholder
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB2aWV3Qm94PSIwIDAgMSAxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNmMGYwZjAiLz48L3N2Zz4=';
  }

  private isInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  // Public methods for React components
  public optimizeReactImage(img: HTMLImageElement) {
    this.optimizeImage(img);
  }

  public updateImage(oldSrc: string, newSrc: string) {
    console.log(`🔄 Updating image: ${oldSrc} → ${newSrc}`);
    
    // Clear cache for old image
    this.cache.delete(oldSrc);
    
    // Update all instances
    const images = document.querySelectorAll(`img[src="${oldSrc}"]`) as NodeListOf<HTMLImageElement>;
    images.forEach(img => {
      if (img && img.src !== undefined) {
        img.src = newSrc;
        img.classList.add('image-loading');
        
        img.onload = () => {
          img.classList.remove('image-loading');
          img.classList.add('image-loaded');
        };
      }
    });
  }

  public clearCache(elementId?: string) {
    if (elementId) {
      // Clear cache for specific element
      console.log(`🗑️ Clearing cache for element: ${elementId}`);
      // Remove any cached URLs that might be associated with this element
      const entries = Array.from(this.cache.entries());
      for (const [key, value] of entries) {
        if (key.includes(elementId) || value.includes(elementId)) {
          this.cache.delete(key);
        }
      }
    } else {
      // Clear entire cache
      console.log('🗑️ Clearing entire image cache');
      this.cache.clear();
    }
  }

  public forceRefreshImage(img: HTMLImageElement, newSrc: string) {
    // Check if img element is valid
    if (!img) {
      console.error('❌ Cannot set properties of null (setting src) - img element is null');
      return;
    }

    console.log(`🔄 Force refreshing image: ${img.src} → ${newSrc}`);
    
    // Clear any loading states
    img.classList.remove('image-loading', 'image-loaded', 'image-error');
    img.classList.add('image-loading');
    
    // For R2 signed URLs, we need to handle them differently
    if (newSrc.includes('r2.cloudflarestorage.com')) {
      // R2 signed URLs are already fresh, just use them directly
      img.src = newSrc;
    } else {
      // For other URLs, force reload by adding timestamp
      const timestamp = Date.now();
      const separator = newSrc.includes('?') ? '&' : '?';
      const refreshedSrc = `${newSrc}${separator}_t=${timestamp}`;
      img.src = refreshedSrc;
    }
    
    // Handle load and error events
    img.onload = () => {
      img.classList.remove('image-loading');
      img.classList.add('image-loaded');
      console.log('✅ Image refreshed successfully');
    };
    
    img.onerror = () => {
      img.classList.remove('image-loading');
      img.classList.add('image-error');
      console.error('❌ Failed to refresh image');
      
      // Fallback to default image if R2 URL fails
      if (newSrc.includes('r2.cloudflarestorage.com')) {
        console.log('🔄 R2 URL failed, trying fallback...');
        const fallbackUrl = newSrc.includes('elements/') 
          ? '/img/elements/default-element.gif'
          : newSrc.includes('characters/')
          ? '/img/characters/default-0.jpg'
          : '/img/pillars/default-0.jpg';
        
        img.src = fallbackUrl;
      }
    };
  }

  public refreshSignedUrls() {
    // Handle both S3 and R2 signed URLs
    const signedImages = document.querySelectorAll('img[src*="amazonaws.com"], img[src*="s3."], img[src*="r2.cloudflarestorage.com"]') as NodeListOf<HTMLImageElement>;
    
    signedImages.forEach(async (img) => {
      const imageKey = img.dataset.imageKey;
      if (imageKey) {
        try {
          // For R2 URLs, we need to get a fresh signed URL from the backend
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/refresh-signed-url/${imageKey}`);
          if (response.ok) {
            const { signedUrl } = await response.json();
            img.src = signedUrl;
          }
        } catch (error) {
          console.error('Failed to refresh signed URL:', error);
        }
      }
    });
  }

  public preloadCriticalImages() {
    // Only preload images that are actually used in the React frontend
    const criticalImages: string[] = [
      // Removed favicon.png and logo-white.png as they're not used in React frontend
      // These images are used in backend templates but not in React components
    ];

    criticalImages.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }
}

// Global instance
const imageLoader = new ReactImageLoader();

// Initialize on page load
if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    // Refresh signed URLs periodically
    setInterval(() => {
      imageLoader.refreshSignedUrls();
    }, 50 * 60 * 1000); // Every 50 minutes
  });
}

export default imageLoader; 